import React, { useState, useEffect } from 'react';
import { propOr, path, isEmpty } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  Card,
  ButtonGroup,
  Form,
  Frame,
  Layout,
  Page,
  Badge,
  Toast,
  Stack,
  FormLayout,
  TextField,
  Checkbox,
  Modal,
  TextContainer,
  Heading,
  Link,
  RangeSlider,
  Select,
} from '@shopify/polaris';
import SuccessBanner from '../components/SuccessBanner';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { CREATE_SHOP_CAREER_PORTAL, UPDATE_SHOP_CAREER_PORTAL } from '../apollo/mutation';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY_OPTIONS, FONT_CASING_OPTIONS } from '../../utils/formDefaultValues';

import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultGrayColor, defaultDarkGrayColor, hslaColor, hexColor } from '../../utils/defaultColors';
import { keysToCamel } from '../../utils/normalizer';

const TalentCommunitySection = ({ history, shopifyToken, shopifyDomain, careerPageLink }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;
  const isSubscribedUser = !isEmpty(currentSubscription);
  const canSwitchFields =
    isSubscribedUser &&
    (currentSubscription.subscriptionType === 'Premium' ||
      currentSubscription.subscriptionType === 'Growth' ||
      currentSubscription.subscriptionType === 'Business');

  const [form, setForm] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const togglePreviewModal = () => setPreview(!preview);
  const toggleConfirmModal = () => setConfirmModal(!confirmModal);
  const [careerPortalTime, setCareerPortalTime] = useState();

  const [formErrors, setFormErrors] = useState({
    talentCommunityHeading: false,
    talentCommunityBody: false,
    talentCommunityButton: false,
  });

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const talentCommunityStyle = propOr({}, 'talent_community_section')(styleControls);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);
    const talentCommunityHeading = propOr('', 'talentCommunityHeading')(careerPortal) || 'Interested but not ready to apply';
    const talentCommunityBody = propOr('', 'talentCommunityBody')(careerPortal) || 'Stay connected!';
    const talentCommunityButton = propOr('', 'talentCommunityButton')(careerPortal) || 'Join our talent community';
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);
    const talentCommunityForm = propOr({}, 'talentCommunityForm')(careerPortal);
    setCareerPortalTime(careerPortal.pageTimestamps);
    setForm((prevForm) => ({
      ...prevForm,
      talentCommunityHeading,
      talentCommunityBody,
      talentCommunityButton,
      talentCommunityForm,
      ...styleControlsFormatted,
      talentCommunitySection,
      talentCommunityStyles: {
        ...prevForm.talentCommunityStyles,
        enabled: true,
        backgroundColor: talentCommunityStyle?.background_color || defaultWhiteColor,
        headingColor: talentCommunityStyle?.heading_color || defaultGrayColor,
        headingWeight: talentCommunityStyle?.heading_weight || '700',
        headingSize: talentCommunityStyle?.heading_size || '26',
        headingFontFamily: talentCommunityStyle?.heading_font_family || 'inherit',
        headingTextCasing: talentCommunityStyle?.heading_text_casing || 'uppercase',
        bodyColor: talentCommunityStyle?.body_color || defaultGrayColor,
        bodyWeight: talentCommunityStyle?.body_weight || '400',
        bodySize: talentCommunityStyle?.body_size || '20',
        bodyFontFamily: talentCommunityStyle?.body_font_family || 'inherit',
        buttonColor: talentCommunityStyle?.button_color || defaultDarkGrayColor,
        buttonTextColor: talentCommunityStyle?.button_text_color || defaultWhiteColor,
        buttonTextWeight: talentCommunityStyle?.button_text_weight || '300',
        buttonTextCasing: talentCommunityStyle?.button_text_casing || 'uppercase',
        buttonFontFamily: talentCommunityStyle?.button_font_family || 'inherit',
      },
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const filterPageTimeLabel = (label) => {
    if (!careerPortalTime) return;
    return careerPortalTime.filter((val) => val.attribute == label)[0].label;
  };

  const updateField = (fieldName) => (value) => {
    let errors = { ...formErrors, [fieldName]: false };
    setFormErrors(errors);
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const updateColor = (fieldName) => (color) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      talentCommunityStyles: {
        ...form.talentCommunityStyles,
        [fieldName]: color,
      },
    });
  };

  const updateFontValue =
    ({ sectionName, fieldName }) =>
    (value) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: `${value}`,
        },
      });
    };

  const toggleSignupFields =
    ({ fieldName, value }) =>
    () => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        talentCommunityForm: {
          ...form.talentCommunityForm,
          [fieldName]: value,
        },
      });
    };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setIsSaveBarActive(false);
    setShowSuccessBanner(true);
    setIsLoading(false);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit =
    (publish = true) =>
    (e) => {
      e?.preventDefault();
      setPreview(false);
      setConfirmModal(false);
      setIsLoading(true);
      const careerPortal = propOr({}, 'careerPortal')(data);
      const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
      const variables = {
        ...careerPortal,
        ...form,
        shopifyToken,
        talentCommunitySection: publish,
      };
      delete variables.styleControls;

      save({ variables }).then(onSuccess).catch(onError);
    };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  return (
    <AppProvider>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { changeRoute('/career-portal-index'); } }]}
          title='Job alert sign up section'
          subtitle={filterPageTimeLabel('talent_community_section')}
          titleMetadata={
            <Badge status={form.talentCommunitySection ? 'success' : 'default'}>
              {form.talentCommunitySection ? 'Published' : 'Unpublished'}
            </Badge>
          }
          pagination={{
            hasPrevious: !isSaveBarActive,
            hasNext: !isSaveBarActive,
            nextURL: !isSaveBarActive ? `/career-portal/job-description-page` : null,
            previousURL: !isSaveBarActive ? `/career-portal/job-listings-section` : null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button plain monochrome onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button
                primary
                disabled={!isSaveBarActive && form.talentCommunitySection}
                onClick={onSubmit(true)}
                connectedDisclosure={
                  form.talentCommunitySection
                    ? {
                        accessibilityLabel: 'Change section status',
                        actions: [{ content: 'Unpublish', onAction: toggleConfirmModal }],
                      }
                    : undefined
                }>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit()}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Job alert sign up section updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={careerPageLink}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection
                title='Content'
                description={
                  <>
                    <p style={{ marginBottom: '10px' }}>
                      Stay connected to possible applicants giving them a space to subscribe to your job openings.
                    </p>
                  </>
                }>
                <Card sectioned>
                  <Card.Section>
                    <FormLayout>
                      <div>
                        <TextField
                          id='talentCommunityHeading'
                          label='Title text'
                          value={form.talentCommunityHeading}
                          name='talentCommunityHeading'
                          onChange={updateField('talentCommunityHeading')}
                          placeholder='Interested but not ready to apply'
                        />
                        {formErrors.talentCommunityHeading && (
                          <InlineError message='Title text is required' fieldID='talentCommunityHeading' />
                        )}
                      </div>
                      <div>
                        <TextField
                          id='talentCommunityBody'
                          label='Message text'
                          value={form.talentCommunityBody}
                          name='talentCommunityBody'
                          onChange={updateField('talentCommunityBody')}
                          placeholder='Stay connected!'
                        />
                        {formErrors.talentCommunityBody && (
                          <InlineError message='Message text is required' fieldID='talentCommunityBody' />
                        )}
                      </div>
                      <div>
                        <TextField
                          id='talentCommunityButton'
                          label='Button text'
                          value={form.talentCommunityButton}
                          name='talentCommunityButton'
                          onChange={updateField('talentCommunityButton')}
                          placeholder='Join our talent community'
                        />
                        {formErrors.talentCommunityButton && (
                          <InlineError message='Button text is required' fieldID='talentCommunityButton' />
                        )}
                      </div>
                    </FormLayout>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and page background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='SECTION'>
                    <ColorPicker
                      colorName='Background color'
                      defaultColor={form.talentCommunityStyles?.backgroundColor}
                      onSelectColor={updateColor('backgroundColor')}
                    />
                  </Card.Section>
                  <Card.Section title='TITLE TEXT'>
                    <ColorPicker
                      colorName='Title text color'
                      defaultColor={form.talentCommunityStyles?.headingColor}
                      onSelectColor={updateColor('headingColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Title font size'
                        value={form.talentCommunityStyles?.headingSize}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'headingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.talentCommunityStyles?.headingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'headingFontFamily' })}
                        value={form.talentCommunityStyles?.headingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'headingWeight' })}
                        value={form.talentCommunityStyles?.headingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'headingTextCasing' })}
                        value={form.talentCommunityStyles?.headingTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='MESSAGE TEXT'>
                    <ColorPicker
                      colorName='Job text color'
                      defaultColor={form.talentCommunityStyles?.bodyColor}
                      onSelectColor={updateColor('bodyColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Message font size'
                        value={form.talentCommunityStyles?.bodySize}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'bodySize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.talentCommunityStyles?.bodySize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Message font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'bodyFontFamily' })}
                        value={form.talentCommunityStyles?.bodyFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Message font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'bodyWeight' })}
                        value={form.talentCommunityStyles?.bodyWeight}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='BUTTON'>
                    <ColorPicker
                      colorName='Button color'
                      defaultColor={form.talentCommunityStyles?.buttonColor}
                      onSelectColor={updateColor('buttonColor')}
                    />
                    <ColorPicker
                      colorName='Button text color'
                      defaultColor={form.talentCommunityStyles?.buttonTextColor}
                      onSelectColor={updateColor('buttonTextColor')}
                    />
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'buttonFontFamily' })}
                        value={form.talentCommunityStyles?.buttonFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button text font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'buttonTextWeight' })}
                        value={form.talentCommunityStyles?.buttonTextWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Button text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'talentCommunityStyles', fieldName: 'buttonTextCasing' })}
                        value={form.talentCommunityStyles?.buttonTextCasing}
                      />
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Sign up form fields'
                description={
                  <>
                    <p style={{ marginBottom: '10px' }}>
                      Select the fields job alert subscribers need to complete when signing up
                    </p>
                    {!canSwitchFields && (
                      <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                        Upgrade account plan
                      </Link>
                    )}
                  </>
                }>
                <Card sectioned>
                  <Card.Section>
                    <div className='job-application-row'>
                      <h2 className='Polaris-TextStyle--variationStrong'>Resume</h2>
                      <ButtonGroup segmented>
                        <Button
                          disabled={!canSwitchFields}
                          primary={form?.talentCommunityForm?.resume === 'required' && canSwitchFields}
                          onClick={toggleSignupFields({ fieldName: 'resume', value: 'required' })}>
                          Required
                        </Button>
                        <Button
                          disabled={!canSwitchFields}
                          primary={form?.talentCommunityForm?.resume === 'optional' && canSwitchFields}
                          onClick={toggleSignupFields({ fieldName: 'resume', value: 'optional' })}>
                          Optional
                        </Button>
                        <Button
                          disabled={!canSwitchFields}
                          primary={form?.talentCommunityForm?.resume === 'disabled' || !canSwitchFields}
                          onClick={toggleSignupFields({ fieldName: 'resume', value: 'disabled' })}>
                          Disabled
                        </Button>
                      </ButtonGroup>
                    </div>
                  </Card.Section>
                  <Card.Section>
                    <div className='job-application-row'>
                      <h2 className='Polaris-TextStyle--variationStrong'>Newsletter sign up</h2>
                      <ButtonGroup segmented>
                        <Button
                          disabled={!canSwitchFields}
                          primary={form?.talentCommunityForm?.newsletter_sign_up !== 'disabled' || !canSwitchFields}
                          onClick={toggleSignupFields({ fieldName: 'newsletter_sign_up', value: 'enabled' })}>
                          Enabled
                        </Button>
                        <Button
                          disabled={!canSwitchFields}
                          primary={form?.talentCommunityForm?.newsletter_sign_up === 'disabled' || !canSwitchFields}
                          onClick={toggleSignupFields({ fieldName: 'newsletter_sign_up', value: 'disabled' })}>
                          Disabled
                        </Button>
                      </ButtonGroup>
                    </div>
                  </Card.Section>
                  <Card.Section>
                    <div className='job-application-row'>
                      <h2 className='Polaris-TextStyle--variationStrong'>Category</h2>
                      <ButtonGroup segmented>
                        <Button
                          disabled={!canSwitchFields}
                          primary={form?.talentCommunityForm?.category !== 'disabled' || !canSwitchFields}
                          onClick={toggleSignupFields({ fieldName: 'category', value: 'enabled' })}>
                          Enabled
                        </Button>
                        <Button
                          disabled={!canSwitchFields}
                          primary={form?.talentCommunityForm?.category === 'disabled' || !canSwitchFields}
                          onClick={toggleSignupFields({ fieldName: 'category', value: 'disabled' })}>
                          Disabled
                        </Button>
                      </ButtonGroup>
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive && form.talentCommunitySection}>
                Publish
              </Button>
            </div>
          </Form>
          <Modal
            open={!!preview}
            large
            onClose={togglePreviewModal}
            primaryAction={{
              content: 'Publish',
              disabled: !isSaveBarActive && form.talentCommunitySection,
              onAction: onSubmit(true),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: togglePreviewModal,
              },
            ]}
            title={
              <TextContainer>
                <Heading>Preview talent community section</Heading>
                <p style={{ fontSize: '0.85em' }}>
                  Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
                  section will look on your store
                </p>
              </TextContainer>
            }
            loading={false}>
            <div className={'preview__modal'}>
              <section
                className='talent_community_section'
                style={{
                  backgroundColor: `${hslaColor(form.talentCommunityStyles?.backgroundColor, '#fff')}`,
                }}>
                <div className='wrapper'>
                  <h1
                    className='heading_text'
                    style={{
                      color: `${hslaColor(form.talentCommunityStyles?.headingColor, '#3a3a3a')}`,
                      fontSize: `${form.talentCommunityStyles?.headingSize || '26'}px`,
                      fontWeight: `${form.talentCommunityStyles?.headingWeight || '700'}`,
                    }}>
                    {form.talentCommunityHeading || 'Interested but not ready to apply'}
                  </h1>
                  <h2
                    className='heading_text'
                    style={{
                      color: `${hslaColor(form.talentCommunityStyles?.bodyColor, '#3a3a3a')}`,
                      fontSize: `${form.talentCommunityStyles?.bodySize || '20'}px`,
                      fontWeight: `${form.talentCommunityStyles?.bodyWeight || '400'}`,
                    }}>
                    {form.talentCommunityBody || 'Stay connected!'}
                  </h2>
                  <a
                    className={'shophire_button'}
                    style={{
                      color: `${hslaColor(form.talentCommunityStyles?.buttonTextColor, '#3a3a3a')}`,
                      backgroundColor: `${hslaColor(form.talentCommunityStyles?.buttonColor, '#3a3a3a')}`,
                    }}>
                    {form.talentCommunityButton || 'Join our talent community'}
                  </a>
                </div>
              </section>
            </div>
          </Modal>
          <Modal
            open={confirmModal}
            onClose={toggleConfirmModal}
            title={`${form.talentCommunitySection ? 'Unpublish' : 'Publish'} section`}
            primaryAction={{
              content: `Yes, ${form.talentCommunitySection ? 'unpublish' : 'publish'} section`,
              onAction: onSubmit(!form.talentCommunitySection),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: toggleConfirmModal,
              },
            ]}
            loading={false}>
            <Modal.Section>
              <p>{`Do you want to ${
                form.talentCommunitySection ? 'unpublish' : 'publish'
              } this section to your career portal page?`}</p>
            </Modal.Section>
          </Modal>
        </Page>
        {toastMarkup}
      </Frame>
    </AppProvider>
  );
};

export default TalentCommunitySection;
